import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";

export type TooltipPosition = "top" | "bottom" | "left" | "right";

export interface TooltipProps {
	/** Tooltip body. Nothing renders when this is empty. */
	text?: React.ReactNode;
	/** Preferred side. Flips to the opposite side when it would leave the viewport. @default bottom */
	position?: TooltipPosition;
	/** Suppress the tooltip without changing the markup around it. */
	disabled?: boolean;
	/**
	 * Wrap in a block-level trigger instead of an inline one. Needed when the
	 * child is a full-width block — a list row, a panel header — that would
	 * otherwise shrink to its text.
	 */
	block?: boolean;
	/** Lands on the trigger wrapper, so `styled(Tooltip)` works. */
	className?: string;
	children: React.ReactNode;
}

/** Distance between the trigger's edge and the tooltip card. */
const GAP = 10;
/** Smallest distance the card keeps from the viewport edge. */
const MARGIN = 6;
/** Half the arrow square's side — how far it pokes out of the card. */
const ARROW = 5;
const SHOW_DELAY_MS = 120;

const OPPOSITE: Record<TooltipPosition, TooltipPosition> = {
	top: "bottom",
	bottom: "top",
	left: "right",
	right: "left",
};

interface Placement {
	left: number;
	top: number;
	side: TooltipPosition;
	/** Arrow centre, in px from the card's left edge (top/bottom) or top edge (left/right). */
	arrow: number;
}

/**
 * Place the card beside `anchor`, flipping to the opposite side when the
 * preferred one does not fit and clamping the result into the viewport. The
 * card can slide sideways when clamped, so the arrow is positioned separately —
 * it stays on the trigger's centre, which is what makes it read as pointing.
 */
function computePlacement(
	anchor: DOMRect,
	card: { width: number; height: number },
	preferred: TooltipPosition,
): Placement {
	const vw = document.documentElement.clientWidth;
	const vh = document.documentElement.clientHeight;

	const fits = (side: TooltipPosition) => {
		switch (side) {
			case "top":
				return anchor.top - GAP - card.height >= MARGIN;
			case "bottom":
				return anchor.bottom + GAP + card.height <= vh - MARGIN;
			case "left":
				return anchor.left - GAP - card.width >= MARGIN;
			case "right":
			default:
				return anchor.right + GAP + card.width <= vw - MARGIN;
		}
	};

	const side = fits(preferred)
		? preferred
		: fits(OPPOSITE[preferred])
		? OPPOSITE[preferred]
		: preferred;

	const vertical = side === "top" || side === "bottom";
	let left: number;
	let top: number;
	if (vertical) {
		left = anchor.left + anchor.width / 2 - card.width / 2;
		top = side === "top" ? anchor.top - GAP - card.height : anchor.bottom + GAP;
	} else {
		left = side === "left" ? anchor.left - GAP - card.width : anchor.right + GAP;
		top = anchor.top + anchor.height / 2 - card.height / 2;
	}

	const clampedLeft = Math.min(Math.max(MARGIN, left), Math.max(MARGIN, vw - card.width - MARGIN));
	const clampedTop = Math.min(Math.max(MARGIN, top), Math.max(MARGIN, vh - card.height - MARGIN));

	const arrow = vertical
		? Math.min(Math.max(10, anchor.left + anchor.width / 2 - clampedLeft), Math.max(10, card.width - 10))
		: Math.min(Math.max(10, anchor.top + anchor.height / 2 - clampedTop), Math.max(10, card.height - 10));

	return { left: clampedLeft, top: clampedTop, side, arrow };
}

/**
 * The one tooltip in the app — a white card per DESIGN_SYSTEM.md §4.
 *
 * Replaces two implementations that were each wrong in their own way:
 *
 *  - `components/ui`'s Tooltip appended a bare `<div class="tooltip">` to
 *    `document.body` on mouseover and removed it on mouseout. When the trigger
 *    disappeared while hovered — a detail panel closing, a button hiding right
 *    after the click that was still under the cursor — the mouseout never
 *    fired and the card stayed orphaned on the page until something else
 *    happened to hover a tooltip.
 *  - antd's Tooltip takes its inner `min-height` from `controlHeight`, which is
 *    48px here (antdTheme.ts). That is where the tall, half-empty toolbar
 *    tooltips came from.
 *
 * The card here is a React portal owned by the trigger, so unmounting the
 * trigger unmounts the tooltip — there is nothing left to orphan. It also
 * closes on pointer-down, scroll, Escape and window blur, which covers the
 * cases where the trigger survives but the pointer has effectively left.
 */
const Tooltip = ({
	text,
	position = "bottom",
	disabled = false,
	block = false,
	className,
	children,
}: TooltipProps) => {
	const triggerRef = useRef<HTMLSpanElement>(null);
	const cardRef = useRef<HTMLDivElement>(null);
	const timerRef = useRef<number | undefined>(undefined);

	const [anchor, setAnchor] = useState<DOMRect | null>(null);
	const [placement, setPlacement] = useState<Placement | null>(null);

	const active = anchor !== null;
	const enabled = !disabled && text !== undefined && text !== null && text !== "";

	const hide = useCallback(() => {
		window.clearTimeout(timerRef.current);
		timerRef.current = undefined;
		setAnchor(null);
		setPlacement(null);
	}, []);

	const show = useCallback(() => {
		if (!enabled) return;
		window.clearTimeout(timerRef.current);
		timerRef.current = window.setTimeout(() => {
			const el = triggerRef.current;
			if (!el) return;
			setAnchor(el.getBoundingClientRect());
		}, SHOW_DELAY_MS);
	}, [enabled]);

	/**
	 * Keyboard focus opens the tooltip; a mouse click must not. Clicking a
	 * button focuses it, and focus fires *after* the pointer-down that just
	 * dismissed the card — without this the tooltip would pop back up over
	 * every button the moment it was pressed.
	 */
	const handleFocus = useCallback(
		(e: React.FocusEvent) => {
			const el = e.target as HTMLElement;
			try {
				if (!el.matches(":focus-visible")) return;
			} catch {
				/* :focus-visible unsupported — fall through and show. */
			}
			show();
		},
		[show],
	);

	// A pending show must not fire after the trigger is gone. The open card
	// needs no such cleanup: it is a child of this component, so React takes it
	// down with the trigger — which is the whole fix for the orphaned tooltip.
	useEffect(() => () => window.clearTimeout(timerRef.current), []);

	// The text can change while the card is open (the distance-mode button
	// relabels itself on every click), so re-measure instead of keeping a stale
	// size.
	useLayoutEffect(() => {
		if (!anchor || !cardRef.current) return;
		const { offsetWidth, offsetHeight } = cardRef.current;
		setPlacement(computePlacement(anchor, { width: offsetWidth, height: offsetHeight }, position));
	}, [anchor, position, text]);

	useEffect(() => {
		if (!active) return undefined;
		const onDismiss = () => hide();
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") hide();
		};
		// Capture phase for scroll: the panels and the library list scroll, not
		// the window, and scroll does not bubble.
		window.addEventListener("scroll", onDismiss, true);
		window.addEventListener("resize", onDismiss);
		window.addEventListener("blur", onDismiss);
		window.addEventListener("pointerdown", onDismiss, true);
		window.addEventListener("keydown", onKeyDown);
		return () => {
			window.removeEventListener("scroll", onDismiss, true);
			window.removeEventListener("resize", onDismiss);
			window.removeEventListener("blur", onDismiss);
			window.removeEventListener("pointerdown", onDismiss, true);
			window.removeEventListener("keydown", onKeyDown);
		};
	}, [active, hide]);

	return (
		<StyledTrigger
			ref={triggerRef}
			className={className}
			$block={block}
			onMouseEnter={show}
			onMouseLeave={hide}
			onFocus={handleFocus}
			onBlur={hide}
		>
			{children}
			{active &&
				enabled &&
				createPortal(
					<StyledTooltipCard
						ref={cardRef}
						role="tooltip"
						$side={placement?.side ?? position}
						$arrow={placement?.arrow ?? 0}
						style={
							placement
								? { left: placement.left, top: placement.top }
								: // First paint is off-screen and invisible: the card has to be
								  // laid out before it can be measured, and measuring is what
								  // decides where it belongs.
								  { left: 0, top: 0, visibility: "hidden" }
						}
					>
						{text}
					</StyledTooltipCard>,
					document.body,
				)}
		</StyledTrigger>
	);
};

const StyledTrigger = styled.span<{ $block: boolean }>`
	/* The trigger is a layout pass-through: no hit area, no baseline gap, and no
	   flex size of its own beyond what the wrapped control already had. */
	display: flex;
	max-width: 100%;
	${({ $block }) =>
		$block
			? `width: 100%; flex-direction: column;`
			: `display: inline-flex; flex: 0 0 auto;`}
`;

const StyledTooltipCard = styled.div<{ $side: TooltipPosition; $arrow: number }>`
	position: fixed;
	/* Above everything it can be triggered from: antd's overlays top out at 1070,
	   and the pixel-streaming button groups sit at 9999 to clear the SDK's own
	   overlays. A tooltip is always the topmost layer. */
	z-index: var(--z-tooltip);
	pointer-events: none;
	max-width: 260px;
	padding: 5px 10px;
	border-radius: var(--radius-md);
	background: var(--color-surface);
	color: var(--color-text);
	font-family: var(--font-family);
	font-size: var(--font-size-caption);
	font-weight: var(--font-weight-regular);
	line-height: 1.4;
	text-align: left;
	white-space: normal;
	/* §4: a tooltip is a floating layer, so it carries the one shadow in the
	   system — and, not being a menu panel, no border alongside it. */
	box-shadow: var(--shadow-modal);

	&::after {
		content: "";
		position: absolute;
		width: ${ARROW * 2}px;
		height: ${ARROW * 2}px;
		background: var(--color-surface);
		transform: rotate(45deg);
		${({ $side, $arrow }) => {
			switch ($side) {
				case "top":
					return `bottom: -${ARROW}px; left: ${$arrow - ARROW}px;`;
				case "bottom":
					return `top: -${ARROW}px; left: ${$arrow - ARROW}px;`;
				case "left":
					return `right: -${ARROW}px; top: ${$arrow - ARROW}px;`;
				case "right":
				default:
					return `left: -${ARROW}px; top: ${$arrow - ARROW}px;`;
			}
		}}
	}
`;

export default Tooltip;
